import type { OpticalMeasurePointAdjustmentState } from '../../index'
import type {
  MeasurementFlowPhase,
  MeasurementFlowViewState,
  MeasurementUiMessage,
  MeasurementUiOvalState,
  PdFlowViewState,
  PdMeasurementResult,
  ShFlowViewState,
  ShMeasurementResult,
} from '../types'

/**
 * Guiding/processing progress tops out at 90% — the final 10% is reserved
 * for the confirmed result, so the bar only fills (and shows its success
 * state) once review is reached.
 */
const GUIDING_PROGRESS_MAX = 0.9

type GuidingPhase = Exclude<MeasurementFlowPhase, 'complete' | 'failed'>

/**
 * Guiding-phase mapping onto the `@ui/features/measurement/Messages`
 * vocabulary. Many-to-one on purpose — the UI vocabulary is coarser than the
 * capture phases (e.g. `findingFrame` covers every "rim not found yet" reason
 * except the glasses-presence one, so it stays on `holdStill`).
 * `complete`/`failed` are mapped by status instead.
 */
const MESSAGE_FOR_PHASE: Record<GuidingPhase, MeasurementUiMessage> = {
  idle: 'initial',
  calibrating: 'initial',
  detecting: 'start',
  noGlasses: 'noGlasses',
  findingFrame: 'holdStill',
  tooClose: 'tooClose',
  tooFar: 'tooFar',
  positioning: 'tooTilted',
  opening: 'openEyes',
  stabilising: 'holdStill',
  sampling: 'scanning',
}

/**
 * Guiding-phase mapping onto the `@ui/features/measurement/Oval` states:
 * user-correctable problems are `warning`, neutral positioning/instructions
 * stay `initial`, active capture is `scanning`.
 */
const OVAL_STATE_FOR_PHASE: Record<GuidingPhase, MeasurementUiOvalState> = {
  idle: 'initial',
  calibrating: 'initial',
  detecting: 'initial',
  // `initial`, not `warning`, to match the message's own oval state in
  // `@ui/features/measurement/Oval` — putting the glasses on is an instruction,
  // not a failed frame.
  noGlasses: 'initial',
  findingFrame: 'initial',
  tooClose: 'warning',
  tooFar: 'warning',
  positioning: 'warning',
  opening: 'warning',
  stabilising: 'scanning',
  sampling: 'scanning',
}

type ViewStateInput<Result> = {
  phase: MeasurementFlowPhase
  progress: number
  result: Result | null
}

/**
 * Shared PD/SH mapping from capture state to the flow's single UI view state.
 * PD and SH differ only in the payload `Result` (and SH's extra `adjustment`,
 * layered on by `deriveShViewState`), so the status/message/oval/progress logic
 * lives here once.
 */
const deriveViewState = <Result>({
  phase,
  progress,
  result,
}: ViewStateInput<Result>): MeasurementFlowViewState<Result> => {
  if (phase === 'failed') {
    return {
      status: 'failed',
      phase,
      progress: 0,
      result: null,
      message: null,
      ovalState: 'warning',
    }
  }

  if (phase === 'complete') {
    if (!result) {
      return {
        status: 'processing',
        phase,
        progress: GUIDING_PROGRESS_MAX,
        result: null,
        message: 'analyzing',
        ovalState: 'scanning',
      }
    }

    return {
      status: 'review',
      phase,
      progress: 1,
      result,
      message: 'completed',
      ovalState: 'completed',
    }
  }

  return {
    status: 'guiding',
    phase,
    progress: progress * GUIDING_PROGRESS_MAX,
    result: null,
    message: MESSAGE_FOR_PHASE[phase],
    ovalState: OVAL_STATE_FOR_PHASE[phase],
  }
}

export type ShViewStateInput = ViewStateInput<ShMeasurementResult> & {
  adjustment?: OpticalMeasurePointAdjustmentState
}

export const deriveShViewState = ({
  adjustment,
  ...input
}: ShViewStateInput): ShFlowViewState => {
  const viewState = deriveViewState(input)
  if (viewState.status !== 'review') return viewState

  return { ...viewState, adjustment }
}

export type PdViewStateInput = ViewStateInput<PdMeasurementResult>

export const derivePdViewState = (input: PdViewStateInput): PdFlowViewState =>
  deriveViewState(input)
